/**
 * useAuth Hook (DC-890)
 *
 * Re-exports the useAuth hook from AuthContext for cleaner imports.
 * Also provides additional auth-related hooks.
 */

export { useAuth, type AuthUser, type UserRole, type LoginCredentials, type RegisterData, type InvitationDetails, type MFARequired, type LoginResult } from '../contexts/AuthContext';

import { useAuth } from '../contexts/AuthContext';
import { useCallback, useMemo } from 'react';

/**
 * Hook to check if user has specific permission(s)
 */
export function usePermissions() {
  const { hasPermission, hasRole, user } = useAuth();

  const canCreate = useMemo(
    () => hasPermission('journey:create') || hasPermission('dataset:create'),
    [hasPermission]
  );

  const canEdit = useMemo(
    () => hasPermission('journey:update') || hasPermission('dataset:update'),
    [hasPermission]
  );

  const canDelete = useMemo(
    () => hasPermission('journey:delete') || hasPermission('dataset:delete'),
    [hasPermission]
  );

  const canManageUsers = useMemo(() => hasRole('admin'), [hasRole]);

  const isAdmin = useMemo(() => hasRole('admin'), [hasRole]);

  const isAnalyst = useMemo(() => hasRole(['admin', 'analyst']), [hasRole]);

  const isViewer = useMemo(() => hasRole('viewer'), [hasRole]);

  return {
    user,
    hasPermission,
    hasRole,
    canCreate,
    canEdit,
    canDelete,
    canManageUsers,
    isAdmin,
    isAnalyst,
    isViewer,
  };
}

/**
 * Hook to get authorization header for API requests
 */
export function useAuthHeader() {
  const { accessToken } = useAuth();

  const getAuthHeader = useCallback((): Record<string, string> => {
    if (!accessToken) return {};
    return { Authorization: `Bearer ${accessToken}` };
  }, [accessToken]);

  return { getAuthHeader, accessToken };
}

/**
 * Hook to make authenticated API requests
 */
export function useAuthenticatedFetch() {
  const { accessToken, refreshToken, logout } = useAuth();

  const authFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const headers = new Headers(options.headers);

      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      let response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      // If 401, try to refresh token and retry
      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          // Retry with new token
          const newHeaders = new Headers(options.headers);
          newHeaders.set('Authorization', `Bearer ${accessToken}`);
          response = await fetch(url, {
            ...options,
            headers: newHeaders,
            credentials: 'include',
          });
        } else {
          // Refresh failed, logout
          await logout();
        }
      }

      return response;
    },
    [accessToken, refreshToken, logout]
  );

  return { authFetch };
}
